/**
 * Stone Investment Area Overview
 * 
 * Area description section with Stone Investment's luxury aesthetic:
 * - Editorial typography
 * - Two-column layout on larger screens
 * - Elegant dividers
 */

'use client';

export interface StoneAreaOverviewProps {
  /**
   * Area data
   */
  area: {
    name: string;
    description?: string;
    highlights?: string[];
  };
  /**
   * Additional content to display
   */
  additionalContent?: string;
  /**
   * Translation messages
   */
  messages?: Record<string, string>;
  /**
   * Custom className
   */
  className?: string;
}

/**
 * StoneAreaOverview - Area description section
 */
export function StoneAreaOverview({
  area,
  additionalContent,
  messages = {},
  className = '',
}: StoneAreaOverviewProps) {
  return (
    <div className={`bg-white p-8 md:p-12 ${className}`}>
      {/* Section Header */}
      <div className="mb-8">
        <p className="text-[10px] uppercase tracking-[0.3em] text-stone-500 font-sans mb-3">
          {messages.aboutLabel || 'About the Area'}
        </p>
        <h2 
          className="font-serif text-3xl md:text-4xl text-stone-900"
          style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
        >
          {area.name}
        </h2>
      </div>
      
      {/* Content */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
        {/* Main Description */}
        {area.description && (
          <div>
            <p
              className="font-sans text-stone-600 leading-relaxed text-base md:text-lg"
              data-speakable
            >
              {area.description}
            </p>
          </div>
        )}
        
        {/* Additional Content */}
        {additionalContent && (
          <div>
            <p className="font-sans text-stone-600 leading-relaxed text-base md:text-lg">
              {additionalContent}
            </p>
          </div>
        )}
      </div>
      
      {/* Highlights */}
      {area.highlights && area.highlights.length > 0 && (
        <div className="mt-12 pt-8 border-t border-stone-200">
          <h3 className="font-serif text-xl text-stone-900 mb-6 italic">
            {messages.highlightsLabel || 'Key Highlights'}
          </h3>
          <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {area.highlights.map((highlight, index) => (
              <li key={index} className="flex items-start gap-3">
                <span className="w-1.5 h-1.5 bg-stone-400 mt-2 flex-shrink-0" />
                <span className="font-sans text-sm text-stone-600">{highlight}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export default StoneAreaOverview;
